/**
 * Certificate Template Component
 * Generates a printable certificate with consistent styling
 */

import React, { memo } from 'react';
import { CeoSignatureIcon } from '../Icons/Icons';

/**
 * The same Academy logo assets the site header renders, from the `header`
 * content type (main_header_branding_elements.dark_mode_logo / light_mode_logo).
 *
 * These are hardcoded because the certificate page is a client component with no
 * CMS access. Keep the stack API key (`bltebc53cfaf0dd6403`) in sync with the
 * header: the previous URL here carried a stale key, so the CDN answered 412
 * ("api_key is not valid"), which made html-to-image reject and left the
 * certificate stuck on a spinner.
 */
const ACADEMY_LOGO_DARK =
  'https://images.contentstack.io/v3/assets/bltebc53cfaf0dd6403/bltb652890207e09c44/6913ff6d9ff683685ba752dd/logo.svg';
const ACADEMY_LOGO_LIGHT =
  'https://images.contentstack.io/v3/assets/bltebc53cfaf0dd6403/bltee1873dded83174d/69156c636f78931b0c7d0359/Academy_Light.svg';

/** Contentstack mark shown beside the wordmark in the footer. Intrinsic 482x404. */
const CONTENTSTACK_AVATAR =
  'https://images.contentstack.io/v3/assets/bltebc53cfaf0dd6403/ama3c50c2d1f77e027/1fe0675528ae9ad2130eaa86/Avatar_favicon.png';

interface CertificateTemplateProps {
  userName: string;
  learningPathTitle: string;
  issueDate: string;
  certificateId: string;
}

const CertificateTemplate = memo(function CertificateTemplate({
  userName,
  learningPathTitle,
  issueDate,
  certificateId,
}: CertificateTemplateProps) {
  // Adjust font size for long names
  const userNameFontSize = userName.length > 30 ? '28px' : '32px';

  return (
    <div className="certificate-container relative">
      {/* Gradient Sidebar */}
      <div className="relative w-[160px] ">
        <div className="certificate-gradient-1 absolute top-0 left-0" />
        <div className="certificate-gradient-2 absolute top-[-25%] right-[-20%]" />
      </div>

      {/* Main Content */}
      <div className="certificate-content">
        {/* Header */}
        <div className="certificate-header">
          {/* Both variants render; CSS shows the one matching the theme, as the header does */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={ACADEMY_LOGO_DARK}
            alt="Contentstack Academy"
            width={180}
            height={16}
            className="certificate-logo certificate-logo--dark"
            crossOrigin="anonymous"
            loading="eager"
            decoding="sync"
          />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={ACADEMY_LOGO_LIGHT}
            alt="Contentstack Academy"
            width={180}
            height={16}
            className="certificate-logo certificate-logo--light"
            crossOrigin="anonymous"
            loading="eager"
            decoding="sync"
          />
          <div className="certificate-title">Certificate of completion</div>
        </div>

        {/* Recipient Information */}
        <div className="certificate-recipient">
          <div>
            <div className="cs-caption">This certificate is awarded to</div>
            <div className="certificate-user-name" style={{ fontSize: userNameFontSize }}>
              {userName}
            </div>
          </div>
          <div>
            <div className="cs-caption">has successfully completed</div>
            <div className="certificate-course-title">{learningPathTitle}</div>
          </div>
        </div>

        {/* Achievement Description */}
        <div className="certificate-description cs-caption">
          <div>
            This individual has demonstrated exceptional dedication and proactive drive for
            enhancing their expertise and career growth.
          </div>
          <div className="mt-2 font-bold">Congratulations on this achievement!</div>
        </div>

        {/* Signature Section */}
        <div className="certificate-signature">
          <CeoSignatureIcon width={140} height={48} />
          <div>
            <h5 className="cs-h5">Neha Sampat</h5>
            <h6 className="cs-h6">Co-Founder and CEO</h6>
          </div>
        </div>

        {/* Footer Information */}
        <div className="certificate-footer">
          <div className="certificate-meta">
            <div>
              Issue date: <span>{issueDate}</span>
            </div>
            <div>
              Certificate ID: <span>{certificateId}</span>
            </div>
          </div>
          <div className="certificate-brand">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={CONTENTSTACK_AVATAR}
              alt=""
              width={17}
              height={14}
              className="certificate-brand-avatar"
              crossOrigin="anonymous"
              loading="eager"
              decoding="sync"
            />
            <span>Contentstack</span>
          </div>
        </div>
      </div>
    </div>
  );
});

export default CertificateTemplate;
